import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Search, TrendingUp, Loader2, Plus, CheckCircle2, Image as ImageIcon, Video, Mic,
  LayoutGrid, Network, GitFork, AlertTriangle, Upload, Pencil, Check, X, Landmark, Sparkles, Link2,
} from "lucide-react";
import { PlannerTab } from "./PlannerTab.js";
import { BacklinksTab } from "./BacklinksTab.js";
import {
  api, summariseAiAssign, type KeywordResearchRow, type ClusterRollup, type ResearchKeywordRow, type LinkGraph, type LinkGraphNode,
  type LinkEdgeKind, type LinkSuggestion, type CoverageReport,
} from "../../lib/api.js";
import { ClusterPicker, type ClusterOption } from "../../components/ui/ClusterPicker.js";
import { fileToCsv, SPREADSHEET_ACCEPT } from "../../lib/spreadsheet.js";

type Tab = "planner" | "opportunities" | "keywords" | "clusters" | "map" | "backlinks" | "coverage";

const IMAGE_KINDS = new Set(["hero", "in_content", "social_card", "thumbnail", "scene_image"]);
const VIDEO_KINDS = new Set(["short_vertical", "square_feed", "long_horizontal", "scene_video", "viral_final"]);
const VOICE_KINDS = new Set(["voiceover_long", "voiceover_scene"]);

function assetCounts(kinds: string[]) {
  let images = 0, videos = 0, voice = 0;
  for (const k of kinds) {
    if (IMAGE_KINDS.has(k)) images++;
    else if (VIDEO_KINDS.has(k)) videos++;
    else if (VOICE_KINDS.has(k)) voice++;
  }
  return { images, videos, voice };
}

export function KeywordResearchPage() {
  const [tab, setTab] = useState<Tab>("planner");
  return (
    <div className="space-y-4 p-6">
      <header className="flex items-center gap-2">
        <TrendingUp className="h-5 w-5 text-brand" />
        <h1 className="text-xl font-semibold text-ink">Keyword Research</h1>
        <span className="text-sm text-muted">— demand, clusters, coverage & links</span>
      </header>

      <SeedBox />
      <AddImportBox />

      <div className="flex gap-1 border-b border-line">
        <TabBtn id="planner" tab={tab} setTab={setTab} icon={Landmark} label="Planner" />
        <TabBtn id="opportunities" tab={tab} setTab={setTab} icon={TrendingUp} label="Opportunities" />
        <TabBtn id="clusters" tab={tab} setTab={setTab} icon={LayoutGrid} label="Clusters" />
        <TabBtn id="keywords" tab={tab} setTab={setTab} icon={Search} label="All keywords" />
        <TabBtn id="map" tab={tab} setTab={setTab} icon={Network} label="Link map" />
        <TabBtn id="backlinks" tab={tab} setTab={setTab} icon={Link2} label="Backlinks" />
        <TabBtn id="coverage" tab={tab} setTab={setTab} icon={GitFork} label="Coverage" />
      </div>

      {tab === "planner" && <PlannerTab />}
      {tab === "opportunities" && <PlannerTab initialView="priorities" />}
      {tab === "clusters" && <ClustersTab />}
      {tab === "keywords" && <KeywordsTab />}
      {tab === "map" && <LinkMapTab />}
      {tab === "backlinks" && <BacklinksTab />}
      {tab === "coverage" && <CoverageTab />}
    </div>
  );
}

function TabBtn({ id, tab, setTab, icon: Icon, label }: { id: Tab; tab: Tab; setTab: (t: Tab) => void; icon: typeof Search; label: string }) {
  const active = tab === id;
  return (
    <button
      onClick={() => setTab(id)}
      className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition ${active ? "border-brand font-medium text-ink" : "border-transparent text-muted hover:text-ink"}`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

/* ─────────────────────────── Seed research (global) ─────────────────────── */
function SeedBox() {
  const qc = useQueryClient();
  const [seed, setSeed] = useState("");
  const [mode, setMode] = useState<"expand" | "exact">("expand");
  const [lang, setLang] = useState<"he" | "en">("he");
  const lookup = useMutation({
    mutationFn: () => api.lookupKeywordResearch({ seed: seed.trim(), mode, language: lang }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["keyword-research"] }),
  });
  return (
    <div className="card flex flex-wrap items-end gap-2 p-3">
      <div className="min-w-[220px] flex-1">
        <label className="label">Research a seed</label>
        <input
          className="input" value={seed} dir={lang === "he" ? "rtl" : "ltr"}
          placeholder={mode === "expand" ? "e.g. רפלקסולוגיה → ~100 related keywords + volumes" : "exact phrase(s), comma-separated"}
          onChange={(e) => setSeed(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && seed.trim()) lookup.mutate(); }}
        />
      </div>
      <select className="input w-auto" value={mode} onChange={(e) => setMode(e.target.value as "expand" | "exact")}>
        <option value="expand">Expand</option><option value="exact">Exact</option>
      </select>
      <select className="input w-auto" value={lang} onChange={(e) => setLang(e.target.value as "he" | "en")}>
        <option value="he">Hebrew</option><option value="en">English</option>
      </select>
      <button className="btn-primary flex items-center gap-1" disabled={!seed.trim() || lookup.isPending} onClick={() => lookup.mutate()}>
        {lookup.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Research
      </button>
      {lookup.data && <span className="text-xs text-accent">+{lookup.data.added} · ${lookup.data.cost.toFixed(3)}</span>}
      {lookup.error && <span className="text-xs text-danger">{(lookup.error as Error).message}</span>}
    </div>
  );
}

/* ─────────────────────────── Add / import keywords ──────────────────────── */
function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "", q = false;
  for (const ch of line) {
    if (ch === '"') q = !q;
    else if (ch === "," && !q) { out.push(cur); cur = ""; }
    else cur += ch;
  }
  out.push(cur);
  return out.map((s) => s.trim().replace(/^"|"$/g, ""));
}
function toNum(s: string | undefined): number | null {
  if (!s) return null;
  const n = parseInt(s.replace(/[,\s]/g, ""), 10);
  return Number.isFinite(n) ? n : null;
}
function normImport(s: string | undefined): string | null {
  const v = (s ?? "").toLowerCase().trim();
  if (v.startsWith("info")) return "informational";
  if (v.startsWith("comm")) return "commercial";
  if (v.startsWith("trans")) return "transactional";
  if (v.startsWith("nav")) return "navigational";
  return null;
}
function parseKeywordCsv(text: string): { keyword: string; searchVolume?: number | null; keywordDifficulty?: number | null; searchIntent?: string | null }[] {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length === 0) return [];
  const first = parseCsvLine(lines[0] ?? "").map((c) => c.toLowerCase());
  // Exact-match the keyword column so "Seed keyword" / "Keyword Difficulty" never win it.
  const kwHeaders = ["keyword", "keywords", "query", "term", "phrase", "target keyword", "search term", "מילת מפתח", "מילות מפתח", "ביטוי", "ביטוי מפתח"];
  const hasHeader = first.some((c) => kwHeaders.includes(c));
  let kwIdx = 0, volIdx = -1, kdIdx = -1, intentIdx = -1, start = 0;
  if (hasHeader) {
    start = 1;
    kwIdx = first.findIndex((c) => kwHeaders.includes(c));
    if (kwIdx < 0) kwIdx = 0;
    // Difficulty/intent checked so "keyword difficulty" doesn't get read as volume etc.
    kdIdx = first.findIndex((c) => /difficulty|\bkd\b|\bsd\b|קושי/.test(c));
    intentIdx = first.findIndex((c) => /intent|כוונ/.test(c));
    volIdx = first.findIndex((c, i) => i !== kdIdx && /volume|^vol\.?$|\bsearches\b|monthly\s*search|נפח/.test(c));
  }
  const rows: { keyword: string; searchVolume?: number | null; keywordDifficulty?: number | null; searchIntent?: string | null }[] = [];
  for (let i = start; i < lines.length; i++) {
    const cells = parseCsvLine(lines[i] ?? "");
    const keyword = (cells[kwIdx] ?? "").trim();
    if (keyword.length < 2) continue;
    rows.push({
      keyword,
      searchVolume: volIdx >= 0 ? toNum(cells[volIdx]) : null,
      keywordDifficulty: kdIdx >= 0 ? toNum(cells[kdIdx]) : null,
      searchIntent: intentIdx >= 0 ? normImport(cells[intentIdx]) : null,
    });
  }
  return rows;
}

function AddImportBox() {
  const qc = useQueryClient();
  const [kw, setKw] = useState("");
  // DFS volume fetching is MANUAL-ONLY (operator decision) — default off; use
  // the "Fetch volumes" button in All keywords for batch/specific lookups.
  const [enrich, setEnrich] = useState(false);
  const [fileErr, setFileErr] = useState<string | null>(null);
  const imp = useMutation({
    mutationFn: (rows: { keyword: string; searchVolume?: number | null; keywordDifficulty?: number | null; searchIntent?: string | null }[]) =>
      api.importKeywordResearch({ rows, enrich, language: "he" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["keyword-research"] }),
  });
  function addSingle() {
    if (!kw.trim()) return;
    imp.mutate([{ keyword: kw.trim() }]);
    setKw("");
  }
  return (
    <div className="card flex flex-wrap items-end gap-2 p-3">
      <div className="min-w-[200px] flex-1">
        <label className="label">Add a keyword</label>
        <input
          className="input" value={kw} dir="rtl" placeholder="single keyword → stored (+ enriched)"
          onChange={(e) => setKw(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") addSingle(); }}
        />
      </div>
      <button className="btn-ghost flex items-center gap-1" disabled={!kw.trim() || imp.isPending} onClick={addSingle}>
        <Plus className="h-4 w-4" /> Add
      </button>
      <label className="btn-ghost flex cursor-pointer items-center gap-1">
        <Upload className="h-4 w-4" /> Import CSV / Excel
        <input type="file" accept={SPREADSHEET_ACCEPT} className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = "";
            if (!f) return;
            setFileErr(null);
            fileToCsv(f)
              .then((t) => {
                const rows = parseKeywordCsv(t);
                if (rows.length) imp.mutate(rows);
                else setFileErr(`No keywords found in "${f.name}". Expected a column headed "keyword" (or one keyword per row).`);
              })
              .catch((err) => setFileErr(`Couldn't read "${f.name}": ${(err as Error).message}`));
          }} />
      </label>
      <label className="flex items-center gap-1 text-xs text-muted">
        <input type="checkbox" checked={enrich} onChange={(e) => setEnrich(e.target.checked)} /> enrich (DataForSEO volumes)
      </label>
      {imp.isPending && <span className="flex items-center gap-1 text-xs text-muted"><Loader2 className="h-3 w-3 animate-spin" /> importing…</span>}
      {imp.error && <span className="text-xs text-danger">{(imp.error as Error).message}</span>}
      {fileErr && <span className="w-full text-xs text-danger">{fileErr}</span>}
      <div className="w-full text-2xs text-muted">CSV: a header row with a <code>keyword</code> column (optional <code>volume</code> / <code>difficulty</code>), or one keyword per line.</div>
      {imp.data && (
        <div className="w-full rounded-lg border border-line bg-canvas p-2.5 text-xs">
          <span className="font-medium text-accent">
            Imported {imp.data.imported} ({imp.data.newCount} new · {imp.data.alreadyStored} already stored)
          </span>
          {imp.data.enriched > 0 && <span className="text-muted"> · enriched {imp.data.enriched} (${imp.data.cost.toFixed(3)})</span>}
          {imp.data.inPipeline.length > 0 && (
            <div className="mt-1 text-amber-700">
              ⚠ {imp.data.inPipeline.length} already in the pipeline:{" "}
              <span dir="rtl">{imp.data.inPipeline.slice(0, 5).map((p) => `${p.keyword} (${p.status})`).join(", ")}</span>
              {imp.data.inPipeline.length > 5 && ` +${imp.data.inPipeline.length - 5} more`}
            </div>
          )}
          {imp.data.published.length > 0 && (
            <div className="mt-1 text-warn">
              ⚠ {imp.data.published.length} already targeted by a LIVE article:{" "}
              <span dir="rtl">{imp.data.published.slice(0, 5).join(", ")}</span>
              {imp.data.published.length > 5 && ` +${imp.data.published.length - 5} more`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── Clusters (A/B/C/E/H) ───────────────────────── */
function ClustersTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["research-clusters"], queryFn: () => api.getResearchClusters() });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [banner, setBanner] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [aiBusy, setAiBusy] = useState<{ ids: Set<string>; progress: string } | null>(null);

  // Every cluster, not only the ones that already hold keywords — the rollup
  // omits empty clusters, which are exactly where a stray keyword may belong.
  const allClusters = useQuery({ queryKey: ["pipeline", "clusters"], queryFn: () => api.listClusters() });
  const clusterOptions: ClusterOption[] = useMemo(
    () => (allClusters.data?.clusters ?? []).map((c) => ({ id: c.id, nameHe: c.nameHe, nameEn: c.nameEn })),
    [allClusters.data],
  );
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["research-clusters"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["pipeline"] });
  };
  const assign = useMutation({
    mutationFn: (clusterId: string | null) => api.bulkAssignCluster([...selected], clusterId),
    onSuccess: () => { setSelected(new Set()); refresh(); },
  });
  const moveOne = useMutation({
    mutationFn: (v: { id: string; clusterId: string | null }) => api.bulkAssignCluster([v.id], v.clusterId),
    onSuccess: refresh,
  });
  const aiAssign = async (ids: string[]) => {
    if (!ids.length) return;
    setAiBusy({ ids: new Set(ids), progress: `0/${ids.length}` });
    try {
      const results = await api.aiAssignClustersAll(ids, (done, total) =>
        setAiBusy({ ids: new Set(ids), progress: `${done}/${total}` }));
      setBanner({ kind: results.some((r) => r.outcome === "error") ? "error" : "ok", text: summariseAiAssign(results) });
      setSelected(new Set());
      refresh();
    } catch (e) {
      setBanner({ kind: "error", text: (e as Error).message });
    } finally {
      setAiBusy(null);
    }
  };

  function toggle(id: string) {
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  if (q.isPending) return <Loading />;
  if (q.error || !q.data) return <div className="text-sm text-danger">Failed to load clusters.</div>;

  return (
    <div className="space-y-3">
      {banner && (
        <div className={`flex items-center gap-2 rounded-md px-3 py-2 text-xs ${banner.kind === "ok" ? "bg-accent/10 text-accent" : "bg-danger/10 text-danger"}`}>
          <span dir="auto">{banner.text}</span>
          <button onClick={() => setBanner(null)} className="ml-auto"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}
      {selected.size > 0 && (
        <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 rounded-lg border border-brand/30 bg-brand/5 px-3 py-2 text-sm">
          <span className="font-medium">{selected.size} selected</span>
          <ClusterPicker
            value={null}
            clusters={clusterOptions}
            onChange={(id) => { if (id) assign.mutate(id); }}
            disabled={assign.isPending || aiBusy !== null}
            busy={assign.isPending}
            compact={false}
            placeholder="Move to cluster…"
          />
          <button className="btn-secondary btn-sm flex items-center gap-1" disabled={aiBusy !== null} onClick={() => aiAssign([...selected])}>
            <Sparkles className="h-3.5 w-3.5" /> {aiBusy ? `Assigning… ${aiBusy.progress}` : "Assign with AI"}
          </button>
          <button className="btn-ghost text-sm text-muted" disabled={assign.isPending} onClick={() => assign.mutate(null)}>Unassign</button>
          <button className="btn-ghost text-sm text-muted" onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}
      {q.data.clusters.map((c) => (
        <ClusterCard key={c.id ?? "none"} c={c} selected={selected} toggle={toggle}
          clusterOptions={clusterOptions} aiBusy={aiBusy}
          onMove={(id, clusterId) => moveOne.mutate({ id, clusterId })}
          onAiAssign={aiAssign} />
      ))}
    </div>
  );
}

function ClusterCard({ c, selected, toggle, clusterOptions, aiBusy, onMove, onAiAssign }: {
  c: ClusterRollup; selected: Set<string>; toggle: (id: string) => void;
  clusterOptions: ClusterOption[]; aiBusy: { ids: Set<string>; progress: string } | null;
  onMove: (id: string, clusterId: string | null) => void;
  onAiAssign: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(true);
  return (
    <div className="card overflow-hidden">
      <div className="flex w-full items-center gap-3 px-4 py-3 hover:bg-canvas">
        <button onClick={() => setOpen((o) => !o)} className="min-w-0 flex-1 text-left">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-ink" dir="rtl">{c.nameHe || c.nameEn}</span>
            {c.pillarStatus && <span className="rounded-full bg-line px-2 py-0.5 text-2xs text-muted">{c.pillarStatus}</span>}
          </div>
          <div className="mt-0.5 text-xs text-muted">
            {c.keywordCount} keywords · {c.totalVolume.toLocaleString()}/mo total demand · {c.withArticle} written · {c.publishedCount} live
          </div>
        </button>
        {/* The unclustered group is the one place a whole card wants the same
            action — every keyword in it is waiting for a home. */}
        {c.id === null && c.keywords.length > 0 && (
          <button
            className="btn-secondary btn-sm flex shrink-0 items-center gap-1"
            disabled={aiBusy !== null}
            onClick={() => onAiAssign(c.keywords.map((k) => k.id))}
            title="Classify every unclustered keyword into its best-fit existing cluster"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {aiBusy ? `Assigning… ${aiBusy.progress}` : `Assign all ${c.keywords.length} with AI`}
          </button>
        )}
        <div className="flex w-40 shrink-0 items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-line">
            <div className="h-full rounded-full bg-accent" style={{ width: `${c.coveragePct}%` }} />
          </div>
          <span className="w-9 text-right text-xs text-muted">{c.coveragePct}%</span>
        </div>
      </div>
      {open && (
        <div className="border-t border-line">
          {c.keywords.map((k) => (
            <KeywordRow key={k.id} k={k} checked={selected.has(k.id)} onToggle={() => toggle(k.id)}
              clusterOptions={clusterOptions} aiRunning={aiBusy?.ids.has(k.id) ?? false} aiLocked={aiBusy !== null}
              onMove={(cid) => onMove(k.id, cid)} onAiAssign={() => onAiAssign([k.id])} />
          ))}
        </div>
      )}
    </div>
  );
}

function KeywordRow({ k, checked, onToggle, clusterOptions, aiRunning, aiLocked, onMove, onAiAssign }: {
  k: ResearchKeywordRow; checked: boolean; onToggle: () => void;
  clusterOptions: ClusterOption[]; aiRunning: boolean; aiLocked: boolean;
  onMove: (clusterId: string | null) => void; onAiAssign: () => void;
}) {
  const a = assetCounts(k.assetKinds);
  return (
    <div className={`flex items-center gap-3 border-b border-line px-4 py-2 text-sm last:border-b-0 ${checked ? "bg-brand/5" : ""}`}>
      <input type="checkbox" checked={checked} onChange={onToggle} className="shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium" dir={k.language === "he" ? "rtl" : "ltr"}>{k.keyword}</div>
        <div className="mt-0.5 flex items-center gap-2 text-2xs text-muted">
          <StageStrip stages={k.stages} />
          <span title="images"><ImageIcon className={`inline h-3 w-3 ${a.images ? "text-accent" : "text-line"}`} /> {a.images || ""}</span>
          <span title="videos"><Video className={`inline h-3 w-3 ${a.videos ? "text-accent" : "text-line"}`} /> {a.videos || ""}</span>
          {a.voice > 0 && <span title="voiceover"><Mic className="inline h-3 w-3 text-accent" /> {a.voice}</span>}
        </div>
      </div>
      <Sparkline data={k.monthlySearches} />
      <div className="w-16 shrink-0 text-right font-mono text-xs">{k.searchVolume == null ? <span className="text-muted">—</span> : `${k.searchVolume.toLocaleString()}`}</div>
      <div className="w-10 shrink-0 text-right font-mono text-2xs text-muted" title="difficulty">{k.keywordDifficulty ?? "–"}</div>
      <div className="hidden w-24 shrink-0 truncate text-2xs text-muted md:block">{k.searchIntent ?? ""}</div>
      <div className="w-14 shrink-0 text-right text-2xs" title="opportunity = volume × (1−difficulty), 0 if live">
        {k.opportunity > 0 ? <span className="font-semibold text-brand">{k.opportunity.toLocaleString()}</span> : <span className="text-muted">—</span>}
      </div>
      <div className="w-8 shrink-0 text-center">
        {k.aiOverview ? <span title="AI Overview present in SERP" className="rounded bg-amber-100 px-1 text-2xs font-semibold text-amber-700">AI</span> : null}
      </div>
      <ClusterPicker
        value={k.clusterId}
        clusters={clusterOptions}
        onChange={onMove}
        onAiAssign={onAiAssign}
        disabled={aiLocked}
        busy={aiRunning}
        placeholder="Unclustered"
      />
    </div>
  );
}

function StageStrip({ stages }: { stages: { key: string; label: string; done: boolean }[] }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {stages.map((s) => (
        <span key={s.key} title={`${s.label}: ${s.done ? "done" : "not yet"}`}
          className={`inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-[8px] font-bold ${s.done ? "bg-accent/15 text-accent" : "bg-line text-muted/60"}`}>
          {s.done ? "✓" : s.label.charAt(0)}
        </span>
      ))}
    </span>
  );
}

function Sparkline({ data }: { data: { year: number; month: number; searchVolume: number }[] | null }) {
  if (!data || data.length < 2) return <div className="w-10 shrink-0" />;
  const pts = data.slice(-12).map((d) => d.searchVolume);
  const max = Math.max(...pts, 1);
  const w = 40, h = 14;
  const path = pts.map((v, i) => `${(i / (pts.length - 1)) * w},${h - (v / max) * h}`).join(" ");
  return (
    <svg width={w} height={h} className="shrink-0">
      <title>12-month trend</title>
      <polyline points={path} fill="none" stroke="currentColor" strokeWidth="1" className="text-brand/60" />
    </svg>
  );
}

/* ─────────────────────────── All keywords (browse) ──────────────────────── */
function KeywordsTab() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("");
  const list = useQuery({ queryKey: ["keyword-research", filter], queryFn: () => api.listKeywordResearch(filter || undefined) });
  const addKw = useMutation({
    mutationFn: (keyword: string) => api.addKeyword({ keyword, language: "he" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["keyword-research"] }),
  });
  // Manual DFS volume fetch — batch (all listed keywords missing a volume) or one keyword.
  const fetchVols = useMutation({
    mutationFn: (keywords: string[]) => api.fetchKeywordVolumes({ keywords, language: "he" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["keyword-research"] }),
  });
  // Manual metric override (volume/KD/intent/CPC) — stored as source 'manual'.
  const editMetric = useMutation({
    mutationFn: (body: Parameters<typeof api.editKeywordMetric>[0]) => api.editKeywordMetric(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["keyword-research"] }),
  });
  const missing = (list.data?.rows ?? []).filter((r) => r.searchVolume == null).map((r) => r.keyword);
  return (
    <div className="card overflow-hidden">
      <div className="flex items-center gap-2 border-b border-line bg-canvas px-4 py-2">
        <Search className="h-3.5 w-3.5 text-muted" />
        <input className="w-full bg-transparent text-sm outline-none" placeholder="Filter stored keywords…" value={filter} onChange={(e) => setFilter(e.target.value)} />
        {fetchVols.data && <span className="shrink-0 text-2xs text-accent">fetched {fetchVols.data.fetched}/{fetchVols.data.requested} · ${fetchVols.data.cost.toFixed(3)}</span>}
        {fetchVols.error && <span className="shrink-0 text-2xs text-danger">{(fetchVols.error as Error).message}</span>}
        {missing.length > 0 && (
          <button
            className="btn-ghost flex shrink-0 items-center gap-1 text-xs"
            disabled={fetchVols.isPending}
            title="One DataForSEO call per 100 keywords — fetches volumes for every listed keyword without one"
            onClick={() => fetchVols.mutate(missing)}
          >
            {fetchVols.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <TrendingUp className="h-3.5 w-3.5" />}
            Fetch volumes ({missing.length})
          </button>
        )}
        <span className="shrink-0 text-xs text-muted">
          {(list.data?.count ?? 0).toLocaleString()} stored
          {list.data && list.data.rows.length < list.data.count && (
            <span className="ml-1 text-2xs text-muted/80" title="The grid shows the highest-volume keywords first. Type in the filter to search the whole corpus.">
              · showing top {list.data.rows.length.toLocaleString()}, filter to find any
            </span>
          )}
        </span>
      </div>
      {list.isPending ? <Loading /> : !list.data || list.data.rows.length === 0 ? (
        <div className="p-10 text-center text-sm text-muted">Nothing researched yet — search a seed above.</div>
      ) : (
        <KeywordsTable rows={list.data.rows} onAdd={(kw) => addKw.mutate(kw)} adding={addKw.isPending}
          onFetchVolume={(kw) => fetchVols.mutate([kw])} fetching={fetchVols.isPending}
          onEdit={(body) => editMetric.mutate(body)} savingEdit={editMetric.isPending} />
      )}
    </div>
  );
}

function KdChip({ kd }: { kd: number | null }) {
  if (kd == null) return <span className="text-2xs text-muted">–</span>;
  const cls = kd <= 30 ? "bg-accent/15 text-accent" : kd <= 60 ? "bg-amber-100 text-amber-700" : "bg-danger/10 text-danger";
  return <span className={`rounded px-1.5 py-0.5 font-mono text-2xs font-semibold ${cls}`}>{kd}</span>;
}

const INTENT_CLS: Record<string, string> = {
  informational: "bg-brand/10 text-brand",
  navigational: "bg-line text-muted",
  commercial: "bg-amber-100 text-amber-700",
  transactional: "bg-indigo-100 text-indigo-700",
};
function IntentChip({ intent }: { intent: string | null }) {
  if (!intent) return <span className="text-2xs text-muted">–</span>;
  return <span className={`rounded-full px-2 py-0.5 text-2xs font-medium ${INTENT_CLS[intent] ?? "bg-line text-muted"}`}>{intent}</span>;
}

const SOURCE_LABEL: Record<string, string> = { dataforseo: "DFS", serpapi: "SERP", csv: "CSV", manual: "MAN" };

function relativeTime(iso: string): string {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (d < 1) return "today";
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { month: "short", day: "numeric" });
}

interface MetricDraft { vol: string; kd: string; intent: string; cpc: string }

function KeywordsTable({ rows, onAdd, adding, onFetchVolume, fetching, onEdit, savingEdit }: {
  rows: KeywordResearchRow[]; onAdd: (kw: string) => void; adding: boolean;
  onFetchVolume: (kw: string) => void; fetching: boolean;
  onEdit: (body: { keyword: string; language?: string; searchVolume?: number | null; keywordDifficulty?: number | null; searchIntent?: string | null; cpc?: number | null }) => void;
  savingEdit: boolean;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<MetricDraft>({ vol: "", kd: "", intent: "", cpc: "" });

  function startEdit(r: KeywordResearchRow) {
    setEditing(r.keyword);
    setDraft({
      vol: r.searchVolume == null ? "" : String(r.searchVolume),
      kd: r.keywordDifficulty == null ? "" : String(r.keywordDifficulty),
      intent: r.searchIntent ?? "",
      cpc: r.cpc == null ? "" : String(r.cpc),
    });
  }
  function saveEdit(r: KeywordResearchRow) {
    const num = (s: string): number | null => (s.trim() === "" ? null : Number(s));
    onEdit({
      keyword: r.keyword,
      language: r.languageCode,
      searchVolume: num(draft.vol),
      keywordDifficulty: num(draft.kd),
      searchIntent: draft.intent.trim() === "" ? null : draft.intent,
      cpc: num(draft.cpc),
    });
    setEditing(null);
  }

  // sqrt scale so the 60K outlier doesn't flatten every other bar.
  const maxVol = Math.max(...rows.map((r) => r.searchVolume ?? 0), 1);
  return (
    <div className="overflow-x-auto">
      <div className="grid min-w-[900px] grid-cols-[1.4fr_64px_180px_52px_120px_70px_80px_110px]">
        <div className="contents text-[10px] font-semibold uppercase tracking-wider text-muted">
          <div className="border-b border-line bg-canvas px-4 py-2">Keyword</div>
          <div className="border-b border-line bg-canvas px-2 py-2">Trend</div>
          <div className="border-b border-line bg-canvas px-2 py-2 text-right">Searches/mo</div>
          <div className="border-b border-line bg-canvas px-2 py-2 text-center" title="Keyword difficulty (0-100)">KD</div>
          <div className="border-b border-line bg-canvas px-2 py-2">Intent</div>
          <div className="border-b border-line bg-canvas px-2 py-2 text-right">CPC</div>
          <div className="border-b border-line bg-canvas px-2 py-2">Fetched</div>
          <div className="border-b border-line bg-canvas px-3 py-2">Pipeline</div>
        </div>
        {rows.map((r) => {
          const barPct = r.searchVolume == null ? 0 : Math.max(4, Math.round((Math.sqrt(r.searchVolume) / Math.sqrt(maxVol)) * 100));
          const isEd = editing === r.keyword;
          return (
            <div key={`${r.keyword}-${r.languageCode}`} className="contents">
              <div className="flex min-w-0 items-center gap-2 border-b border-line px-4 py-2">
                <span className="min-w-0 truncate text-sm font-medium" dir={r.languageCode === "he" ? "rtl" : "ltr"}>{r.keyword}</span>
                <span className="shrink-0 rounded bg-line/60 px-1 font-mono text-[9px] text-muted" title={`source: ${r.source}`}>{SOURCE_LABEL[r.source] ?? r.source}</span>
              </div>
              <div className="flex items-center border-b border-line px-2 py-2"><Sparkline data={r.monthlySearches} /></div>
              <div className="flex items-center justify-end gap-2 border-b border-line px-2 py-2">
                {isEd ? (
                  <input type="number" min={0} className="input w-24 py-0.5 text-right font-mono text-xs" placeholder="volume"
                    value={draft.vol} onChange={(e) => setDraft((d) => ({ ...d, vol: e.target.value }))} />
                ) : r.searchVolume == null ? (
                  <button className="btn-ghost text-2xs text-muted" disabled={fetching}
                    title="Fetch this keyword's volume from DataForSEO (manual call)"
                    onClick={() => onFetchVolume(r.keyword)}>
                    fetch volume
                  </button>
                ) : (
                  <>
                    <div className="h-1.5 w-20 overflow-hidden rounded-full bg-line">
                      <div className="h-full rounded-full bg-brand/70" style={{ width: `${barPct}%` }} />
                    </div>
                    <span className="w-14 text-right font-mono text-xs">{r.searchVolume.toLocaleString()}</span>
                  </>
                )}
              </div>
              <div className="flex items-center justify-center border-b border-line px-2 py-2">
                {isEd ? (
                  <input type="number" min={0} max={100} className="input w-14 py-0.5 text-center font-mono text-xs" placeholder="KD"
                    value={draft.kd} onChange={(e) => setDraft((d) => ({ ...d, kd: e.target.value }))} />
                ) : <KdChip kd={r.keywordDifficulty} />}
              </div>
              <div className="flex items-center border-b border-line px-2 py-2">
                {isEd ? (
                  <select className="input w-full py-0.5 text-xs" value={draft.intent}
                    onChange={(e) => setDraft((d) => ({ ...d, intent: e.target.value }))}>
                    <option value="">—</option>
                    <option value="informational">informational</option>
                    <option value="navigational">navigational</option>
                    <option value="commercial">commercial</option>
                    <option value="transactional">transactional</option>
                  </select>
                ) : <IntentChip intent={r.searchIntent} />}
              </div>
              <div className="flex items-center justify-end border-b border-line px-2 py-2 font-mono text-2xs text-muted">
                {isEd ? (
                  <input type="number" min={0} step={0.01} className="input w-16 py-0.5 text-right font-mono text-xs" placeholder="cpc"
                    value={draft.cpc} onChange={(e) => setDraft((d) => ({ ...d, cpc: e.target.value }))} />
                ) : r.cpc == null ? "–" : `$${r.cpc.toFixed(2)}`}
              </div>
              <div className="flex items-center border-b border-line px-2 py-2 text-2xs text-muted">{relativeTime(r.fetchedAt)}</div>
              <div className="flex items-center gap-1.5 border-b border-line px-3 py-2">
                {isEd ? (
                  <>
                    <button className="btn-ghost p-0.5 text-accent" title="Save (stored as manual override)" disabled={savingEdit}
                      onClick={() => saveEdit(r)}><Check className="h-4 w-4" /></button>
                    <button className="btn-ghost p-0.5 text-muted" title="Cancel" onClick={() => setEditing(null)}><X className="h-4 w-4" /></button>
                  </>
                ) : (
                  <>
                    {r.published ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-accent/10 px-2 py-0.5 text-2xs font-medium text-accent" title="A live article already targets this keyword (Content DB main keyword)">
                        <CheckCircle2 className="h-3 w-3" /> live article
                      </span>
                    ) : r.inPipeline ? (
                      <span className="inline-flex items-center gap-1 text-xs text-brand" title={`Pipeline status: ${r.pipelineStatus ?? "?"}`}>
                        <CheckCircle2 className="h-3.5 w-3.5" /> {r.pipelineStatus ?? "in pipeline"}
                      </span>
                    ) : (
                      <button className="btn-ghost flex items-center gap-1 text-xs" disabled={adding} onClick={() => onAdd(r.keyword)}><Plus className="h-3.5 w-3.5" /> Add</button>
                    )}
                    <button className="btn-ghost ml-auto p-0.5 text-muted hover:text-ink" title="Edit volume / KD / intent / CPC manually"
                      onClick={() => startEdit(r)}><Pencil className="h-3.5 w-3.5" /></button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────── Link map (D) — interactive ─────────────────── */
const EDGE_KIND_META: Record<LinkEdgeKind, { label: string; hint: string }> = {
  contextual: { label: "In-body links", hint: "Hyperlinks inside article text (strongest SEO signal)" },
  related: { label: "Related-articles widget", hint: "The end-of-article relatedArticles references" },
  treatment: { label: "Treatment pages", hint: "Links from treatment/category pages (FAQ, body, related)" },
};

function LinkMapTab() {
  const q = useQuery({ queryKey: ["research-link-graph"], queryFn: () => api.getResearchLinkGraph() });
  const [show, setShow] = useState<Record<LinkEdgeKind, boolean>>({ contextual: true, related: true, treatment: true });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoverId, setHoverId] = useState<string | null>(null);

  if (q.isPending) return <Loading />;
  if (q.error || !q.data) return <div className="text-sm text-danger">Failed to load link graph.</div>;
  const g = q.data;
  const edges = g.edges.filter((e) => show[e.kind]);
  const byId = new Map(g.nodes.map((n) => [n.id, n]));
  const selected = selectedId ? byId.get(selectedId) ?? null : null;
  const kindCounts: Record<LinkEdgeKind, number> = { contextual: 0, related: 0, treatment: 0 };
  for (const e of g.edges) kindCounts[e.kind]++;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <Stat label="Articles" value={g.nodes.filter((n) => n.kind === "article").length} />
        <Stat label="True orphans (no inbound at all)" value={g.orphans.length} danger={g.orphans.length > 0} />
        <Stat label="No in-body inbound" value={g.contextualOrphans.length} danger={false} />
        <div className="ml-auto flex flex-wrap gap-1.5">
          {(Object.keys(EDGE_KIND_META) as LinkEdgeKind[]).map((k) => (
            <button key={k} title={EDGE_KIND_META[k].hint}
              onClick={() => setShow((s) => ({ ...s, [k]: !s[k] }))}
              className={`rounded-full border px-2.5 py-1 text-xs transition ${show[k] ? "border-brand/40 bg-brand/10 text-ink" : "border-line text-muted"}`}>
              {EDGE_KIND_META[k].label} · {kindCounts[k]}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-[1fr_320px]">
        <div className="card p-3">
          <InteractiveGraph g={g} edges={edges} hoverId={hoverId} selectedId={selectedId}
            onHover={setHoverId} onSelect={(id) => setSelectedId((cur) => (cur === id ? null : id))} />
          <div className="mt-2 flex flex-wrap items-center gap-3 text-2xs text-muted">
            <span><span className="mr-1 inline-block h-2.5 w-2.5 rounded-full bg-warn align-middle" /> true orphan</span>
            <span><span className="mr-1 inline-block h-2.5 w-2.5 rounded-full bg-amber-300 align-middle" /> no in-body inbound</span>
            <span><span className="mr-1 inline-block h-2.5 w-2.5 rounded-full bg-brand align-middle" /> article (size = inbound links)</span>
            <span><span className="mr-1 inline-block h-2.5 w-2.5 rounded-sm bg-indigo-400 align-middle" /> treatment page</span>
            <span>hover to trace · click for details</span>
          </div>
        </div>

        <NodePanel node={selected} g={g} onPick={setSelectedId} />
      </div>

      <FixLinksPanel g={g} />
    </div>
  );
}

function InteractiveGraph({ g, edges, hoverId, selectedId, onHover, onSelect }: {
  g: LinkGraph; edges: LinkGraph["edges"]; hoverId: string | null; selectedId: string | null;
  onHover: (id: string | null) => void; onSelect: (id: string) => void;
}) {
  const size = 620, r = 265, cx = size / 2, cy = size / 2;
  const pos = new Map<string, { x: number; y: number }>();
  g.nodes.forEach((n, i) => {
    const ang = (i / g.nodes.length) * Math.PI * 2 - Math.PI / 2;
    pos.set(n.id, { x: cx + r * Math.cos(ang), y: cy + r * Math.sin(ang) });
  });
  const focus = hoverId ?? selectedId;
  const connected = new Set<string>();
  if (focus) {
    connected.add(focus);
    for (const e of edges) {
      if (e.from === focus) connected.add(e.to);
      if (e.to === focus) connected.add(e.from);
    }
  }
  const trueOrphan = new Set(g.orphans);
  const ctxOrphan = new Set(g.contextualOrphans);

  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="mx-auto block max-h-[620px] w-full max-w-[620px]"
      onMouseLeave={() => onHover(null)}>
      {edges.map((e, i) => {
        const a = pos.get(e.from), b = pos.get(e.to);
        if (!a || !b) return null;
        const active = focus != null && (e.from === focus || e.to === focus);
        const base = e.kind === "contextual" ? "" : e.kind === "related" ? "4 3" : "1.5 2.5";
        return (
          <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y}
            strokeDasharray={base || undefined}
            className={active ? "stroke-brand" : "stroke-line"}
            strokeWidth={active ? 1.6 : e.kind === "contextual" ? 0.9 : 0.55}
            opacity={focus ? (active ? 0.95 : 0.08) : e.kind === "contextual" ? 0.6 : 0.35} />
        );
      })}
      {g.nodes.map((n) => {
        const p = pos.get(n.id)!;
        const totalIn = n.inContextual + n.inRelated + n.inTreatment;
        const rad = Math.min(4 + Math.sqrt(totalIn) * 1.6, 13);
        const dim = focus != null && !connected.has(n.id);
        const fill = trueOrphan.has(n.id) ? "fill-warn" : n.kind === "treatment" ? "fill-indigo-400" : ctxOrphan.has(n.id) ? "fill-amber-300" : "fill-brand";
        const common = {
          className: `${fill} cursor-pointer transition-opacity`,
          opacity: dim ? 0.18 : 1,
          onMouseEnter: () => onHover(n.id),
          onClick: () => onSelect(n.id),
        };
        return n.kind === "treatment" ? (
          <rect key={n.id} x={p.x - rad * 0.9} y={p.y - rad * 0.9} width={rad * 1.8} height={rad * 1.8} rx={2} {...common}>
            <title>{`${n.title}\nin: ${totalIn} (body ${n.inContextual} · widget ${n.inRelated} · treatment ${n.inTreatment}) · out: ${n.out}`}</title>
          </rect>
        ) : (
          <circle key={n.id} cx={p.x} cy={p.y} r={rad} stroke={selectedId === n.id ? "currentColor" : "none"} strokeWidth={2} {...common}>
            <title>{`${n.title}\nin: ${totalIn} (body ${n.inContextual} · widget ${n.inRelated} · treatment ${n.inTreatment}) · out: ${n.out}`}</title>
          </circle>
        );
      })}
    </svg>
  );
}

function NodePanel({ node, g, onPick }: { node: LinkGraphNode | null; g: LinkGraph; onPick: (id: string) => void }) {
  if (!node) {
    return (
      <div className="card flex items-center justify-center p-6 text-center text-sm text-muted">
        Click a node to see exactly who links to it and where it links out.
      </div>
    );
  }
  const byId = new Map(g.nodes.map((n) => [n.id, n]));
  const inbound = g.edges.filter((e) => e.to === node.id);
  const outbound = g.edges.filter((e) => e.from === node.id);
  const KindTag = ({ k }: { k: LinkEdgeKind }) => (
    <span className={`rounded px-1 text-[9px] font-semibold uppercase ${k === "contextual" ? "bg-brand/10 text-brand" : k === "related" ? "bg-line text-muted" : "bg-indigo-100 text-indigo-700"}`}>
      {k === "contextual" ? "body" : k === "related" ? "widget" : "treatment"}
    </span>
  );
  return (
    <div className="card max-h-[660px] space-y-3 overflow-y-auto p-4 text-sm">
      <div>
        <div className="font-semibold" dir="rtl">{node.title}</div>
        {node.url && <a href={node.url} target="_blank" rel="noreferrer" className="text-xs text-brand hover:underline">{node.url.replace("https://www.alternabe.co.il", "")}</a>}
        <div className="mt-1 text-2xs text-muted">
          inbound {inbound.length} (body {node.inContextual} · widget {node.inRelated} · treatment {node.inTreatment}{node.inTreatmentBody ? `, ${node.inTreatmentBody} in text` : ""}) · outbound {node.out}
        </div>
      </div>
      <div>
        <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-muted">Linked FROM ({inbound.length})</div>
        {inbound.length === 0 ? (
          node.kind === "treatment"
            ? <div className="text-xs text-muted">No inbound links from content (site navigation isn't counted).</div>
            : <div className="text-xs text-warn">Nothing links here — a true orphan.</div>
        ) : (
          <ul className="space-y-1">
            {inbound.map((e, i) => {
              const s = byId.get(e.from);
              return s ? (
                <li key={i} className="flex items-center gap-1.5 text-xs">
                  <KindTag k={e.kind} />
                  <button className="min-w-0 flex-1 truncate text-left hover:text-brand" dir="rtl" onClick={() => onPick(s.id)}>{s.title}</button>
                </li>
              ) : null;
            })}
          </ul>
        )}
      </div>
      <div>
        <div className="mb-1 text-2xs font-semibold uppercase tracking-wide text-muted">Links OUT to ({outbound.length})</div>
        <ul className="space-y-1">
          {outbound.map((e, i) => {
            const t = byId.get(e.to);
            return t ? (
              <li key={i} className="flex items-center gap-1.5 text-xs">
                <KindTag k={e.kind} />
                <button className="min-w-0 flex-1 truncate text-left hover:text-brand" dir="rtl" onClick={() => onPick(t.id)}>{t.title}</button>
              </li>
            ) : null;
          })}
        </ul>
      </div>
    </div>
  );
}

/** Action items: weakly-linked pages → suggested source pages → bulk fix. */
function FixLinksPanel({ g }: { g: LinkGraph }) {
  const qc = useQueryClient();
  const weakIds = useMemo(() => {
    const t = new Set(g.orphans);
    return [...g.orphans, ...g.contextualOrphans.filter((id) => !t.has(id))];
  }, [g]);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const sugg = useQuery({
    queryKey: ["link-suggestions", weakIds.join(",")],
    queryFn: () => api.getLinkSuggestions(weakIds),
    enabled: weakIds.length > 0,
  });
  // The backlink pass runs in the BACKGROUND on the server: one target costs a
  // model call per host page that has no existing mention to wrap, and a dozen
  // targets is minutes of work, so holding the POST open just earned a 504 from
  // the gateway while the links were still being written. The POST now only
  // starts the run; progress and results come from polling, which is also what
  // makes a page refresh mid-run harmless.
  const status = useQuery({
    queryKey: ["fix-links-status"],
    queryFn: () => api.getFixLinksStatus(),
    refetchInterval: (q) => (q.state.data?.status === "running" ? 2000 : false),
  });
  const running = status.data?.status === "running";
  const wasRunning = useRef(false);
  useEffect(() => {
    // A finished run has rewritten live pages — the graph and the suggestions
    // built from it are both stale now.
    if (wasRunning.current && !running) {
      qc.invalidateQueries({ queryKey: ["research-link-graph"] });
      qc.invalidateQueries({ queryKey: ["link-suggestions"] });
    }
    wasRunning.current = Boolean(running);
  }, [running, qc]);

  const fix = useMutation({
    mutationFn: (items: LinkSuggestion[]) =>
      api.fixLinks(items.filter((s) => s.url).map((s) => ({ url: s.url!, title: s.title, keyword: s.keyword, clusterId: s.clusterId }))),
    onSuccess: () => { setChecked(new Set()); void status.refetch(); },
  });

  if (weakIds.length === 0) return null;
  const trueOrphan = new Set(g.orphans);
  const rows = sugg.data?.suggestions ?? [];
  const selectedRows = rows.filter((s) => checked.has(s.sanityId));

  return (
    <div className="card p-4">
      <div className="mb-1 flex items-center gap-1 text-sm font-medium">
        <AlertTriangle className="h-4 w-4 text-warn" /> Fix internal linking ({g.orphans.length} true orphans · {g.contextualOrphans.length} without in-body links)
      </div>
      <p className="mb-3 text-xs text-muted">
        Review the source pages below: the % is the keyword-word match, the tag is the topic check (strong / related / unrelated), and "—" marks a page found by topic alone. Dimmed pages will not get a new sentence. Tick the targets to fix, then Implement — the backlink
        mechanism wraps an existing keyword mention in each source, or adds one short on-brand sentence, linked and anchored on the
        target's main keyword. Up to 5 links per target, idempotent.
      </p>
      {sugg.isPending ? <Loading /> : (
        <div className="space-y-2">
          <div className="overflow-x-auto rounded-lg border border-line">
            <div className="grid min-w-[860px] grid-cols-[36px_1.2fr_110px_150px_1.5fr] items-stretch">
              <div className="contents text-[10px] font-semibold uppercase tracking-wider text-muted">
                <div className="border-b border-line bg-canvas px-2 py-2" />
                <div className="border-b border-line bg-canvas px-3 py-2">Target page</div>
                <div className="border-b border-line bg-canvas px-3 py-2">Inbound status</div>
                <div className="border-b border-line bg-canvas px-3 py-2">Anchor keyword</div>
                <div className="border-b border-line bg-canvas px-3 py-2">Suggested source pages (relevance)</div>
              </div>
              {rows.map((s) => (
                <div key={s.sanityId} className={`contents ${checked.has(s.sanityId) ? "[&>div]:bg-brand/5" : ""}`}>
                  <div className="flex items-start justify-center border-b border-line px-2 py-2.5">
                    <input type="checkbox" checked={checked.has(s.sanityId)}
                      onChange={() => setChecked((c) => { const n = new Set(c); n.has(s.sanityId) ? n.delete(s.sanityId) : n.add(s.sanityId); return n; })} />
                  </div>
                  <div className="min-w-0 border-b border-line px-3 py-2.5">
                    <div className="truncate text-sm font-medium" dir="rtl">{s.title}</div>
                    {s.url && <a href={s.url} target="_blank" rel="noreferrer" className="text-2xs text-brand hover:underline">{s.url.replace("https://www.alternabe.co.il", "")}</a>}
                  </div>
                  <div className="border-b border-line px-3 py-2.5">
                    {trueOrphan.has(s.sanityId)
                      ? <span className="rounded-full bg-warn/10 px-2 py-0.5 text-2xs font-medium text-warn">no inbound at all</span>
                      : <span className="rounded-full bg-amber-100 px-2 py-0.5 text-2xs font-medium text-amber-700">widget-only</span>}
                  </div>
                  <div className="min-w-0 border-b border-line px-3 py-2.5">
                    <div className="truncate text-xs font-medium" dir="rtl">{s.keyword}</div>
                    {!s.hasMainKeyword && <div className="mt-0.5 text-2xs text-warn">no main KW — using title; set one in Content DB</div>}
                  </div>
                  <div className="min-w-0 border-b border-line px-3 py-2.5">
                    {s.candidates.length === 0 ? <span className="text-2xs text-muted">no relevant source pages found (by words or topic)</span> : (
                      <ul className="space-y-0.5">
                        {s.candidates.map((c) => (
                          // Dimmed = the run will not write a new sentence here (topic
                          // judged unrelated, or no verdict and a low word score). It can
                          // still wrap an existing mention of the keyword.
                          <li key={c.sanityId} className={`flex items-center gap-2 text-2xs ${c.mayGenerate === false ? "opacity-45" : ""}`} title={c.topicReason ?? ""}>
                            <span className={`w-9 shrink-0 rounded px-1 text-center font-mono font-semibold ${c.score >= 0.6 ? "bg-accent/15 text-accent" : "bg-line text-muted"}`} title="keyword-word match">
                              {c.via === "topic" ? "—" : `${Math.round(c.score * 100)}%`}
                            </span>
                            {c.topic && (
                              <span className={`shrink-0 rounded px-1 text-[9px] font-semibold uppercase ${c.topic === "strong" ? "bg-accent/15 text-accent" : c.topic === "related" ? "bg-brand/10 text-brand" : "bg-line text-muted"}`}>
                                {c.topic === "none" ? "unrelated" : c.topic}
                              </span>
                            )}
                            <span className="min-w-0 truncate" dir="rtl">{c.title}</span>
                            {c.kind !== "article" && <span className="shrink-0 rounded bg-indigo-100 px-1 text-[9px] font-semibold uppercase text-indigo-700">treatment</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                    {s.topicChecked === false && (
                      <div className="mt-1 text-2xs text-warn">
                        {s.diagnostic && /topic check failed/.test(s.diagnostic) ? "Topic check failed — showing word matches only." : "Topic check still running — word matches only for now; reload in a minute."}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <button className="btn-primary flex items-center gap-1 text-sm" disabled={selectedRows.length === 0 || fix.isPending || running}
              onClick={() => fix.mutate(selectedRows)}>
              {fix.isPending || running ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              {running
                ? `Writing backlinks… ${status.data?.done ?? 0}/${status.data?.total ?? 0}`
                : `Implement backlinks for ${selectedRows.length} selected`}
            </button>
            {running && (
              <span className="text-xs text-muted">
                Each target takes up to a minute (one model-written sentence per host page without an existing mention).
                {status.data?.current ? <> Now: <span dir="rtl">{status.data.current}</span></> : null} You can leave this page — the run keeps going.
              </span>
            )}
            {!running && status.data?.status === "done" && (status.data.results?.length ?? 0) > 0 && (
              <span className="text-xs text-accent">
                {status.data.results!.map((r) => `${r.title}: ${r.linked} link(s)${r.error ? ` (error: ${r.error})` : r.linked === 0 && r.diagnostic ? ` (${r.diagnostic})` : ""}`).join(" · ")}
              </span>
            )}
            {!running && status.data?.status === "error" && <span className="text-xs text-danger">{status.data.error}</span>}
            {fix.error && <span className="text-xs text-danger">{(fix.error as Error).message}</span>}
          </div>
          {/* Per host: a high-relevance source that did NOT link has to say why,
              or the operator is left guessing between a model decline, a keyword
              that would not fit a sentence verbatim, and a lost write. */}
          {!running && status.data?.status === "done" && status.data.results?.some((r) => r.hosts?.length) && (
            <div className="space-y-2 pt-1">
              {status.data.results!.filter((r) => r.hosts?.length).map((r) => (
                <div key={r.url} className="rounded-lg border border-line p-2">
                  <div className="mb-1 text-xs font-medium" dir="rtl">{r.title}</div>
                  <ul className="space-y-0.5">
                    {r.hosts!.map((h) => (
                      <li key={h.sanityId} className="flex items-start gap-2 text-2xs">
                        <span className="w-9 shrink-0 rounded bg-line px-1 text-center font-mono font-semibold text-muted" title="keyword-word match">
                          {h.via === "topic" ? "—" : `${Math.round(h.score * 100)}%`}
                        </span>
                        {/* Topic verdict: a page can share no words with the target and
                            still be where its readers come from (healthy eating → oats). */}
                        <span className={`w-16 shrink-0 ${h.topic === "strong" ? "text-accent" : h.topic === "related" ? "text-brand" : "text-muted"}`} title={h.topicReason ?? ""}>
                          {h.topic ? `topic: ${h.topic === "none" ? "no" : h.topic}` : ""}
                        </span>
                        <span className={`w-24 shrink-0 font-medium ${h.outcome === "linked" ? "text-accent" : h.outcome === "skipped" ? "text-warn" : "text-muted"}`}>
                          {h.outcome === "linked" ? "linked" : h.outcome === "already-linked" ? "already links" : h.outcome === "skipped" ? "not linked" : "not reached"}
                        </span>
                        <span className="min-w-0 shrink-0 truncate sm:w-64" dir="rtl">{h.title}</span>
                        {h.detail && <span className="min-w-0 text-muted">{h.detail}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────── Coverage (F) ───────────────────────────────── */
function CoverageTab() {
  const q = useQuery({ queryKey: ["research-coverage"], queryFn: () => api.getResearchCoverage() });
  if (q.isPending) return <Loading />;
  if (q.error || !q.data) return <div className="text-sm text-danger">Failed to load coverage.</div>;
  const c: CoverageReport = q.data;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-4 text-sm">
        <Stat label="Live articles" value={c.publishedCount} />
        <Stat label="Cannibalization groups" value={c.cannibalizationCount} danger={c.cannibalizationCount > 0} />
        <Stat label="Pipeline keywords" value={c.mapping.length} />
      </div>

      <div className="card p-4">
        <div className="mb-2 text-sm font-medium">Cannibalization <span className="text-muted">(two+ live articles on the same main keyword)</span></div>
        {c.cannibalization.length === 0 ? (
          <div className="flex items-center gap-1 text-sm text-accent"><CheckCircle2 className="h-4 w-4" /> None detected.</div>
        ) : (
          <ul className="space-y-2 text-sm">
            {c.cannibalization.map((g) => (
              <li key={g.keyword}>
                <div className="font-medium text-warn" dir="rtl">{g.keyword}</div>
                <ul className="ml-4 list-disc text-xs text-muted">
                  {g.urls.map((u, i) => <li key={i} dir="rtl">{u.url ? <a href={u.url} target="_blank" rel="noreferrer" className="text-brand hover:underline">{u.title}</a> : u.title}</li>)}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="card overflow-hidden">
        <div className="border-b border-line bg-canvas px-4 py-2 text-sm font-medium">Keyword → coverage</div>
        {c.mapping.map((m) => (
          <div key={m.id} className="flex items-center justify-between gap-3 border-b border-line px-4 py-2 text-sm last:border-b-0">
            <span className="min-w-0 flex-1 truncate font-medium" dir="rtl">{m.keyword}</span>
            <span className="text-2xs text-muted">{m.status}</span>
            {m.covered ? <span className="inline-flex items-center gap-1 text-xs text-accent"><CheckCircle2 className="h-3.5 w-3.5" /> live</span>
              : <span className="text-xs text-muted">gap</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────── shared bits ────────────────────────────────── */
function Stat({ label, value, danger }: { label: string; value: number; danger?: boolean }) {
  return (
    <div className="card px-4 py-2">
      <div className={`text-lg font-semibold ${danger ? "text-warn" : "text-ink"}`}>{value.toLocaleString()}</div>
      <div className="text-2xs uppercase tracking-wide text-muted">{label}</div>
    </div>
  );
}
function Loading() {
  return <div className="flex items-center gap-2 p-6 text-sm text-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
}
